import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useDevId } from "@/hooks/useDevId"

import {
  buildOccurredAtISO,
  parseTracesFile,
  parseTranscript,
  validateAudioFile,
  validateTranscriptFile,
  type TracesData,
} from "../utils/audioUploadHelpers"

export const UPLOAD_STATUS = {
  IDLE: "idle",
  UPLOADING: "uploading",
  PROCESSING: "processing",
  COMPLETE: "complete",
  ERROR: "error",
} as const

type UploadStatus = (typeof UPLOAD_STATUS)[keyof typeof UPLOAD_STATUS]

export const ERROR_STEP = {
  UPLOAD: "upload",
  PROCESSING: "processing",
} as const

type ErrorStep = (typeof ERROR_STEP)[keyof typeof ERROR_STEP] | null

/**
 * Owns all upload-form state for the unified uploaded conversation form: the
 * audio, transcript, traces, metadata, occurred-at and dev-environment fields
 * plus upload status and errors.
 */
export function useAudioUploadForm() {
  const transcriptReadGeneration = useRef(0)
  const tracesReadGeneration = useRef(0)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [audioFileError, setAudioFileError] = useState("")
  const [audioUrl, setAudioUrl] = useState("")
  const [transcriptText, setTranscriptText] = useState("")
  const [transcriptFile, setTranscriptFile] = useState<File | null>(null)
  const [transcriptError, setTranscriptError] = useState("")
  const [isTranscriptReading, setIsTranscriptReading] = useState(false)

  const [tracesFile, setTracesFile] = useState<File | null>(null)
  const [tracesData, setTracesData] = useState<TracesData | null>(null)
  const [tracesError, setTracesError] = useState("")
  const [isTracesReading, setIsTracesReading] = useState(false)
  const [tracesWarning, setTracesWarning] = useState("")

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    UPLOAD_STATUS.IDLE,
  )
  const [errorMessage, setErrorMessage] = useState("")
  const [conversationId, setConversationId] = useState("")
  const [metadata, setMetadata] = useState<Record<string, string>>({})
  const [occurredAt, setOccurredAt] = useState("")
  const [errorStep, setErrorStep] = useState<ErrorStep>(null)

  const devId = useDevId()
  const occurredAtISO = useMemo(
    () => buildOccurredAtISO(occurredAt),
    [occurredAt],
  )

  useEffect(
    () => () => {
      transcriptReadGeneration.current += 1
      tracesReadGeneration.current += 1
    },
    [],
  )

  const handleMetadataChange = useCallback(
    (newMetadata: Record<string, string>) => setMetadata(newMetadata),
    [],
  )

  const handleAudioFileChange = useCallback((file: File | null) => {
    if (!file) {
      setAudioFile(null)
      setAudioFileError("")
      return
    }

    const validation = validateAudioFile(file)
    if (!validation.ok) {
      setAudioFile(null)
      setAudioFileError(validation.error)
      return
    }

    setAudioFile(file)
    setAudioFileError("")
  }, [])

  const handleTranscriptFileChange = useCallback(async (file: File | null) => {
    const generation = ++transcriptReadGeneration.current
    if (!file) {
      setTranscriptFile(null)
      setTranscriptText("")
      setTranscriptError("")
      setIsTranscriptReading(false)
      return
    }

    const validation = validateTranscriptFile(file)
    if (!validation.ok) {
      setTranscriptFile(null)
      setTranscriptText("")
      setTranscriptError(validation.error)
      setIsTranscriptReading(false)
      return
    }

    setTranscriptError("")
    setTranscriptFile(file)
    setTranscriptText("")
    setIsTranscriptReading(true)
    try {
      const text = await file.text()
      if (transcriptReadGeneration.current !== generation) {
        return
      }
      if (parseTranscript(text).length === 0) {
        setTranscriptFile(null)
        setTranscriptError(
          "We couldn't read that transcript. Check the format and try again.",
        )
        return
      }
      setTranscriptText(text)
    } catch {
      if (transcriptReadGeneration.current !== generation) {
        return
      }
      setTranscriptFile(null)
      setTranscriptError("We couldn't read this transcript file.")
    } finally {
      if (transcriptReadGeneration.current === generation) {
        setIsTranscriptReading(false)
      }
    }
  }, [])

  const handleTracesFileChange = useCallback(async (file: File | null) => {
    const generation = ++tracesReadGeneration.current
    if (!file) {
      setTracesFile(null)
      setTracesData(null)
      setTracesError("")
      setIsTracesReading(false)
      return
    }

    setTracesFile(null)
    setTracesData(null)
    setTracesError("")
    setTracesWarning("")
    setIsTracesReading(true)

    try {
      const result = await parseTracesFile(file)
      if (tracesReadGeneration.current !== generation) {
        return
      }
      if (!result.ok) {
        setTracesError(result.error)
        return
      }

      setTracesFile(file)
      setTracesData(result.data)
    } finally {
      if (tracesReadGeneration.current === generation) {
        setIsTracesReading(false)
      }
    }
  }, [])

  const resetForm = useCallback(() => {
    transcriptReadGeneration.current += 1
    tracesReadGeneration.current += 1
    setUploadStatus(UPLOAD_STATUS.IDLE)
    setErrorMessage("")
    setErrorStep(null)
    setAudioFile(null)
    setAudioFileError("")
    setAudioUrl("")
    setTranscriptText("")
    setTranscriptFile(null)
    setTranscriptError("")
    setIsTranscriptReading(false)
    setOccurredAt("")
    setMetadata({})
    setTracesFile(null)
    setTracesData(null)
    setTracesError("")
    setIsTracesReading(false)
    setTracesWarning("")
  }, [])

  const isUploading =
    uploadStatus === UPLOAD_STATUS.UPLOADING ||
    uploadStatus === UPLOAD_STATUS.PROCESSING
  const isValidForUpload =
    !isTranscriptReading &&
    !isTracesReading &&
    (!!audioFile || !!audioUrl.trim() || !!transcriptText.trim())

  return {
    audioFile,
    setAudioFile,
    audioFileError,
    audioUrl,
    setAudioUrl,
    transcriptText,
    setTranscriptText,
    transcriptFile,
    transcriptError,
    tracesFile,
    tracesData,
    tracesError,
    isTracesReading,
    tracesWarning,
    setTracesWarning,
    uploadStatus,
    setUploadStatus,
    errorMessage,
    setErrorMessage,
    conversationId,
    setConversationId,
    metadata,
    handleMetadataChange,
    occurredAt,
    setOccurredAt,
    errorStep,
    setErrorStep,
    devId,
    occurredAtISO,
    isUploading,
    isValidForUpload,
    handleAudioFileChange,
    handleTranscriptFileChange,
    handleTracesFileChange,
    resetForm,
  }
}
